import { memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
// Import UI Components
import { Popover, Icon, BusyLoader, Option } from '@geneui/components';
// Import Components
import ButtonWithLoader from 'components/common/ButtonWithLoader';
// Import Hooks
import useOnClickOutside from 'hooks/UseOnClickOutside';
// Import Constants
import { l } from 'constants/common';
// Import Styles
import 'assets/scss/downloadToolbar.scss';

interface Props {
    downloadAction: () => void;
    sendToEmailAction: () => void;
    isFileLoading?: boolean;
    buttonText?: string;
    isDisabledDownload?: boolean;
    shouldCloseOnAction?: boolean;
}

const DownloadOrSendToEmailToolbar: React.FC<Props> = (props) => {
    const {
        buttonText = l.Download,
        isFileLoading = false,
        isDisabledDownload = false,
        shouldCloseOnAction = false,
        downloadAction,
        sendToEmailAction,
    } = props;
    const { t } = useTranslation();
    const toolbarRef = useRef(null);

    const [toolbarVisibilityStatus, setToolbarVisibilityStatus] = useState(false);

    const actionTypes = {
        download: 'download',
        sendtoEmail: 'sendtoEmail',
    };

    const defaultStates = {
        download: false,
        sendtoEmail: false,
    };

    const [isShowLoader, setIsShowLoader] = useState(defaultStates);

    const handleButtonClick = () => {
        setToolbarVisibilityStatus(!toolbarVisibilityStatus);
    };

    const sendToEmailClickHandler = () => {
        sendToEmailAction();
        updateIsShowState(actionTypes.sendtoEmail);
        shouldCloseOnAction && setToolbarVisibilityStatus(false);
    };

    const downloadClickHandler = () => {
        downloadAction();
        updateIsShowState(actionTypes.download);
        shouldCloseOnAction && setToolbarVisibilityStatus(false);
    };

    const updateIsShowState = (key) => {
        const states = defaultStates;
        states[key] = true;
        setIsShowLoader(states);
    };

    useOnClickOutside(
        toolbarRef,
        useCallback(() => {
            if (toolbarVisibilityStatus) {
                setToolbarVisibilityStatus(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [toolbarVisibilityStatus]),
    );

    return (
        <div className="download-send-to-email-toolbar" ref={toolbarRef}>
            <div className="download-send-to-email-toolbar-inner">
                <Popover
                    isOpen={toolbarVisibilityStatus}
                    extendTargetWidth={false}
                    minHeight={277}
                    align="end"
                    scrollbarNeeded={false}
                >
                    <>
                        <ButtonWithLoader
                            className="user-btn d-toolbar-btn"
                            appearance="minimal"
                            onClick={handleButtonClick}
                            isLoading={isFileLoading}
                            isDisabled={isDisabledDownload}
                        >
                            {t(buttonText)}
                            <Icon type={`bc-icon-arrow-${!toolbarVisibilityStatus ? 'down' : 'up'}`} />
                        </ButtonWithLoader>

                        {toolbarVisibilityStatus && (
                            <>
                                <div className="d-toolbar-content">
                                    <div className={classNames(isFileLoading ? 'download-disable' : '')}>
                                        <div className="d-toolbar-menu-row">
                                            {(!isFileLoading || !isShowLoader.download) && (
                                                <Icon type="bc-icon-download" />
                                            )}
                                            {isShowLoader.download && (
                                                <BusyLoader type="spinner" spinnerSize="small" isBusy={isFileLoading} />
                                            )}
                                            <Option title={t(l.DownloadNow)} onClick={downloadClickHandler} />
                                        </div>
                                        <div className="d-toolbar-menu-row">
                                            {(!isFileLoading || !isShowLoader.sendtoEmail) && (
                                                <Icon type="bc-icon-message-closed" />
                                            )}
                                            {isShowLoader.sendtoEmail && (
                                                <BusyLoader type="spinner" spinnerSize="small" isBusy={isFileLoading} />
                                            )}
                                            <Option title={t(l.SendToEmail)} onClick={sendToEmailClickHandler} />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                </Popover>
            </div>
        </div>
    );
};

export default memo(DownloadOrSendToEmailToolbar);
